<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import { CONSTANTS } from "@/util/constants";
import { fieldFullWidth } from "@/util/methods";
import {
  Bell03Icon,
  ClockRewindIcon,
  Dataflow03Icon,
  Grid01Icon,
  Monitor04Icon,
  Phone01Icon,
  PlusIcon,
  ReverseLeftIcon,
  ReverseRightIcon,
  Settings04Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIToolbar, UIToolbarGroup, UITooltip } from "@gohighlevel/ghl-ui";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ManageCategoryModal from "../quizzes/Category/ManageCategoryModal.vue";
import ConditionalLogicModal from "./ConditionalLogicModal.vue";

const showManageCategories = ref(false);
const store = useAppStore();
const emitter = useEmitter();
const { t } = useI18n();

// Store sidebar state
const previousSidebarState = ref({
  leftSidebarOpen: false,
  rightSidebarOpen: false,
  emailSidebarOpen: false,
});

// Check if we're in edit mode
const isEditMode = computed(() => store.activeBuilderTab === "edit");

// Type-safe computed properties for history operations
const canUndo = computed(() => Number(store.historyIndex) > 0);
const canRedo = computed(() => Number(store.historyIndex) < store.history.length - 1);

const goToVersions = () => (store.showVersions = true);

const renderIcon = (icon) => icon;

const deviceOptions = ref([
  {
    key: "desktop",
    icon: renderIcon(Monitor04Icon),
    tooltip: t("common.desktopMode"),
    click: () => {
      switchDeviceMode("desktop");
    },
  },
  {
    key: "mobile",
    icon: renderIcon(Phone01Icon),
    tooltip: t("common.mobileMode"),
    click: () => {
      switchDeviceMode("mobile");
    },
  },
]);

const headerTabs = computed(() => [
  {
    key: "edit",
    label: t("common.edit"),
    active: store.activeBuilderTab === "edit",
    id: "header-tab-edit",
  },
  {
    key: "settings",
    label: t("common.settings"),
    active: store.activeBuilderTab === "settings",
    id: "header-tab-settings",
  },
  {
    key: "submissions",
    label: t("common.submissions"),
    active: store.activeBuilderTab === "submissions",
    id: "header-tab-submissions",
  },
]);

const selectTab = (tab) => {
  const previousTab = store.activeBuilderTab;

  // If switching from edit to another tab, save sidebar state
  if (previousTab === "edit" && tab.key !== "edit") {
    // Store current sidebar state before switching away from edit
    const sidebarState = {
      leftSidebarOpen: false,
      rightSidebarOpen: false,
      emailSidebarOpen: false,
    };

    // Get left sidebar state
    emitter.emit("get-left-sidebar-state", (isOpen) => {
      sidebarState.leftSidebarOpen = isOpen;
    });

    // Get right sidebar state
    emitter.emit("get-right-sidebar-state", (isOpen) => {
      sidebarState.rightSidebarOpen = isOpen;
    });

    // Get email sidebar state
    emitter.emit("get-email-sidebar-state", (isOpen) => {
      sidebarState.emailSidebarOpen = isOpen;
    });

    // Save the state
    previousSidebarState.value = sidebarState;

    // Close sidebars when switching to settings or submissions
    emitter.emit("close-all-sidebars");
  }

  // If switching back to edit, restore previous sidebar state
  if (previousTab !== "edit" && tab.key === "edit") {
    // Restore sidebar state after DOM update to ensure proper rendering
    nextTick(() => {
      emitter.emit("restore-sidebar-state", previousSidebarState.value);
    });
  }

  store.activeBuilderTab = tab.key;
};

const applyLayoutToElements = (layoutIndex: number, isInitialLoad = false) => {
  const validFieldsForWidth = store.elements.filter(
    (field) => !fieldFullWidth(field.type, true)
  ).length;

  let anyChanges = false;

  store.elements.forEach((field) => {
    if (!field.fieldWidthPercentage && !fieldFullWidth(field.type, true)) {
      anyChanges = true;
      if (layoutIndex === CONSTANTS.ONE_COLUMN) {
        field.fieldWidthPercentage = 100;
      } else if (layoutIndex === CONSTANTS.TWO_COLUMN) {
        field.fieldWidthPercentage = 50;
      } else {
        field.fieldWidthPercentage = Math.max(
          parseFloat((100 / validFieldsForWidth).toFixed(2)),
          20
        );
        if (field.type === "submit") {
          field.fullwidth = false;
          field.padding.left = 10;
          field.padding.right = 10;
        }
      }
    }
  });

  if (!isInitialLoad && anyChanges) {
    store.addToHistory();
    store.anyUnsavedChanges = true;
  }
};

const switchDeviceMode = (mode) => {
  store.deviceMode = mode;

  if (!store.builderConfig.mobileLayout) {
    store.builderConfig.mobileLayout = CONSTANTS.ONE_COLUMN;
  }

  // Initialize mobile padding if switching to mobile and it doesn't exist
  if (mode === "mobile" && !store.formStyle.mobilePadding) {
    store.formStyle.mobilePadding = { ...store.formStyle.padding };
  }
};

onMounted(() => {
  const currentLayout =
    store.deviceMode === "mobile"
      ? store.builderConfig.mobileLayout || store.builderConfig.layout
      : store.builderConfig.layout;

  applyLayoutToElements(currentLayout, true);

  nextTick(() => {
    const layoutToApply =
      store.deviceMode === "mobile"
        ? store.builderConfig.mobileLayout || store.builderConfig.layout
        : store.builderConfig.layout;

    applyLayoutToElements(layoutToApply, true);
  });
});

watch(
  () => store.elements.length,
  () => {
    if (store.elements.length > 0) {
      const layoutToApply =
        store.deviceMode === "mobile"
          ? store.builderConfig.mobileLayout || store.builderConfig.layout
          : store.builderConfig.layout;

      applyLayoutToElements(layoutToApply, false);
    }
  }
);
</script>

<template>
  <!-- New layout when isNewBuilderLayoutEnabled is true -->
  <UIToolbar
    v-if="store.isNewBuilderLayoutEnabled"
    class="secondary-header-toolbar mt-[50px] flex items-center border-b bg-white p-0"
  >
    <!-- Left section with add buttons - only visible in edit mode -->
    <UIToolbarGroup v-if="isEditMode" class="ml-8 w-20 justify-between">
      <div class="flex justify-center">
        <div class="border-right flex gap-x-5 px-4">
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="open-left-bar"
                text
                size="small"
                class="text-gray-800"
                @click="emitter.emit('toggle-left-side-bar')"
              >
                <PlusIcon class="h-4 w-4" />
              </UIButton>
            </template>
            {{
              store.formId
                ? t("form.addFormElement")
                : store.surveyId
                ? t("survey.addSurveyElement")
                : t("quiz.addQuizElement")
            }}
          </UITooltip>
          <UITooltip
            v-if="store.formId || (store.surveyId && store.builder.version >= 4)"
            trigger="hover"
            placement="top"
          >
            <template #trigger>
              <UIButton
                id="open-conditional-logic"
                text
                size="small"
                class="text-gray-800"
                @click="store.showConditionalLogicModal = true"
              >
                <Dataflow03Icon class="h-4 w-4" />
              </UIButton>
            </template>
            {{ t("form.openConditionalLogic") }}
          </UITooltip>
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="open-email-settings"
                text
                size="small"
                class="text-gray-800"
                @click="emitter.emit('toggle-email-side-bar')"
              >
                <Bell03Icon class="h-4 w-4" />
              </UIButton>
            </template>
            {{ t("common.showNotificationsSettings") }}</UITooltip
          >
          <UITooltip v-if="store.quizId" trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="open-email-settings"
                text
                size="small"
                class="text-gray-800"
                @click="showManageCategories = true"
              >
                <Grid01Icon class="h-4 w-4" />
              </UIButton>
            </template>
            {{ t("quiz.category.manageCategories") }}</UITooltip
          >
        </div>
      </div>
    </UIToolbarGroup>
    <div v-else class="ml-[-1.75rem] h-[49px] w-20"></div>

    <!-- Device options at extreme left - only visible in edit mode -->
    <div
      v-if="isEditMode"
      class="ml-12 mt-[3px] flex items-center rounded-lg border border-gray-300"
    >
      <UITooltip v-for="(item, index) in deviceOptions" :key="index">
        <template #trigger>
          <UIButton
            :id="`form-builder__btn--${item.key}`"
            class="hl-builder-action-buttons"
            :active="store.deviceMode !== item.key"
            text
            @click="item.click"
          >
            <component :is="item.icon" class="h-5 w-5" />
          </UIButton>
        </template>
        <span>{{ item.tooltip }}</span>
      </UITooltip>
    </div>
    <div v-else class="ml-12 h-[32px] w-[84px]"></div>

    <!-- Flexible spacer -->
    <div class="flex-1"></div>

    <!-- Center section with tabs - centered in remaining space -->
    <div class="flex gap-x-1 pr-[30px]">
      <span
        v-for="tab in headerTabs"
        :id="tab.id"
        :key="tab.key"
        class="font-inter relative inline-block cursor-pointer select-none border-b-0 px-2.5 py-1 text-sm font-semibold leading-relaxed tracking-wide transition-all duration-200 ease-in-out hover:border-b-2 hover:text-primary-700"
        :class="[
          tab.active ? 'border-b-2 border-primary-600 text-primary-700' : 'text-gray-600',
          store.processing ? 'pointer-events-none cursor-not-allowed opacity-50' : '',
        ]"
        @click="!store.processing && selectTab(tab)"
      >
        {{ tab.label }}
      </span>
    </div>

    <!-- Flexible spacer -->
    <div class="flex-1"></div>

    <!-- Right section with other controls - only visible in edit mode -->
    <UIToolbarGroup v-if="isEditMode" class="mr-4 justify-center">
      <div class="flex items-center justify-center">
        <div class="border-right">
          <UITooltip placement="top">
            <template #trigger>
              <UIButton
                :id="`builder__btn--version`"
                class="py-4 text-gray-800"
                quaternary
                :disabled="!store.builder?.versionHistory?.length"
                @click="goToVersions"
              >
                <ClockRewindIcon class="h-5 w-5"
              /></UIButton>
            </template>
            <span>{{ t("common.versions") }}</span>
          </UITooltip>
        </div>

        <div class="flex items-center justify-around">
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="btn--undo"
                text
                size="small"
                class="pl-4 pr-2 text-gray-800"
                :disabled="!canUndo"
                @click="store.undo"
              >
                <ReverseLeftIcon class="h-5 w-5" />
              </UIButton>
            </template>
            {{ t("common.undo") }}
          </UITooltip>
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="btn--redo"
                text
                class="pl-2 pr-4 text-gray-800"
                :disabled="!canRedo"
                @click="store.redo"
              >
                <ReverseRightIcon class="h-5 w-5" />
              </UIButton>
            </template>
            {{ t("common.redo") }}
          </UITooltip>
        </div>
        <UITooltip trigger="hover" placement="top">
          <template #trigger>
            <UIButton
              id="open-right-bar"
              text
              size="small"
              class="border-left p-3 text-gray-800"
              @click="emitter.emit('toggle-right-side-bar')"
            >
              <Settings04Icon class="h-4 w-4" />
            </UIButton>
          </template>
          {{ t("common.styles") }} & {{ t("common.options") }}
        </UITooltip>
      </div>
    </UIToolbarGroup>
    <div v-else class="mr-4 h-[49px] w-[140px]"></div>
  </UIToolbar>

  <!-- Original layout when isNewBuilderLayoutEnabled is false -->
  <UIToolbar v-else class="secondary-header-toolbar mt-[50px] border-b bg-white p-0">
    <UIToolbarGroup v-if="isEditMode" class="ml-8 w-20 justify-between">
      <div class="flex justify-center">
        <div class="border-right flex gap-x-5 px-4">
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="open-left-bar"
                text
                size="small"
                class="text-gray-800"
                @click="emitter.emit('toggle-left-side-bar')"
              >
                <PlusIcon class="h-4 w-4" />
              </UIButton>
            </template>
            {{
              store.formId
                ? t("form.addFormElement")
                : store.surveyId
                ? t("survey.addSurveyElement")
                : t("quiz.addQuizElement")
            }}
          </UITooltip>
          <UITooltip
            v-if="store.formId || (store.surveyId && store.builder.version >= 4)"
            trigger="hover"
            placement="top"
          >
            <template #trigger>
              <UIButton
                id="open-conditional-logic"
                text
                size="small"
                class="text-gray-800"
                @click="store.showConditionalLogicModal = true"
              >
                <Dataflow03Icon class="h-4 w-4" />
              </UIButton>
            </template>
            {{ t("form.addConditionalLogic") }}
          </UITooltip>
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="open-email-settings"
                text
                size="small"
                class="text-gray-800"
                @click="emitter.emit('toggle-email-side-bar')"
              >
                <Bell03Icon class="h-4 w-4" />
              </UIButton>
            </template>
            {{ t("common.showNotificationsSettings") }}</UITooltip
          >
          <UITooltip v-if="store.quizId" trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="open-email-settings"
                text
                size="small"
                class="text-gray-800"
                @click="showManageCategories = true"
              >
                <Grid01Icon class="h-4 w-4" />
              </UIButton>
            </template>
            {{ t("quiz.category.manageCategories") }}</UITooltip
          >
        </div>
      </div>
    </UIToolbarGroup>
    <div v-else class="border-right ml-8 h-[49px] w-20"></div>

    <UIToolbarGroup v-if="isEditMode" class="ml-16 justify-center">
      <div class="flex items-center rounded-lg border border-gray-300">
        <UITooltip v-for="(item, index) in deviceOptions" :key="index">
          <template #trigger>
            <UIButton
              :id="`form-builder__btn--${item.key}`"
              class="hl-builder-action-buttons"
              :active="store.deviceMode !== item.key"
              text
              @click="item.click"
            >
              <component :is="item.icon" class="h-5 w-5" />
            </UIButton>
          </template>
          <span>{{ item.tooltip }}</span>
        </UITooltip>
      </div>
    </UIToolbarGroup>
    <div v-else class="ml-16 h-[32px] w-[84px]"></div>

    <UIToolbarGroup class="justify-center">
      <div class="flex items-center justify-center">
        <div v-if="isEditMode" class="border-right">
          <UITooltip placement="top">
            <template #trigger>
              <UIButton
                :id="`builder__btn--version`"
                class="py-4 text-gray-800"
                quaternary
                :disabled="!store.builder?.versionHistory?.length"
                @click="goToVersions"
              >
                <ClockRewindIcon class="h-5 w-5"
              /></UIButton>
            </template>
            <span>{{ t("common.versions") }}</span>
          </UITooltip>
        </div>
        <div v-else class="h-[49px] w-[49px]"></div>

        <!-- Header tabs removed from old layout -->

        <div v-if="isEditMode" class="flex items-center justify-around">
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="btn--undo"
                text
                size="small"
                class="pl-4 pr-2 text-gray-800"
                :disabled="!canUndo"
                @click="store.undo"
              >
                <ReverseLeftIcon class="h-5 w-5" />
              </UIButton>
            </template>
            {{ t("common.undo") }}
          </UITooltip>
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="btn--redo"
                text
                class="pl-2 pr-4 text-gray-800"
                :disabled="!canRedo"
                @click="store.redo"
              >
                <ReverseRightIcon class="h-5 w-5" />
              </UIButton>
            </template>
            {{ t("common.redo") }}
          </UITooltip>
        </div>
        <div v-else class="h-[36px] w-[100px]"></div>

        <UITooltip v-if="isEditMode" trigger="hover" placement="top">
          <template #trigger>
            <UIButton
              id="open-right-bar"
              text
              size="small"
              class="border-left p-3 text-gray-800"
              @click="emitter.emit('toggle-right-side-bar')"
            >
              <Settings04Icon class="h-4 w-4" />
            </UIButton>
          </template>
          {{ t("common.styles") }} & {{ t("common.options") }}
        </UITooltip>
        <div v-else class="h-[36px] w-[36px]"></div>
      </div>
    </UIToolbarGroup>
  </UIToolbar>
  <ConditionalLogicModal
    :show="store.showConditionalLogicModal"
    @update:close-modal="store.showConditionalLogicModal = false"
  />
  <ManageCategoryModal
    v-if="showManageCategories"
    :show="showManageCategories"
    @update:close-modal="showManageCategories = false"
  ></ManageCategoryModal>
</template>

<style scoped>
.secondary-header-toolbar {
  border-color: #e2e2e2 !important;
}

.border-right {
  border-right: 1px solid #dee2e6 !important;
  height: 49px;
}

.border-left {
  border-left: 1px solid #dee2e6 !important;
}

.hl-toolbar-group {
  padding: 0px !important;
}

.hl-builder-action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
}
</style>
